// Third Party
import * as React from 'react';
import { Transition, TransitionChild } from '@headlessui/react';
import ReactDOM from 'react-dom';
import { parseCookies, setCookie } from 'nookies';
import { useSession } from 'next-auth/react';

// Utils
import { useTimeoutFn } from '@hooks';
import { routes } from '@routes';

// UI
import { Typography } from '@components/typography';
import { Button } from '@components/button';

type Props = {};

const HIDE_MODAL_KEY = '__savvy_should_hide_middleman_modal__';

function isModalHidden(): boolean {
  const cookies = parseCookies();

  return cookies[HIDE_MODAL_KEY] === 'true';
}

function persistHideModal(): void {
  setCookie(null, HIDE_MODAL_KEY, 'true', {
    maxAge: 360 * 24 * 60 * 60, // 1 year
    path: '/',
  });
}

const MiddlemanModalPrivate: React.FC<Props> = () => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const { data: session, status } = useSession();
  const isAuthenticated = status === 'authenticated' && !!session;

  // If modal is active add class to body to prevent scroll
  React.useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add('modalActive');
    } else {
      document.body.classList.remove('modalActive');
    }
  }, [isModalOpen]);

  useTimeoutFn(() => {
    // Only show modal if user is not logged in and modal hasn't been hidden
    if (!isModalHidden() && !isAuthenticated) {
      setIsModalOpen(true);
    }
  }, 500);

  const dismissModalHandler = React.useCallback(() => {
    persistHideModal();
    setIsModalOpen(false);
  }, []);

  const handleEscapeKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeModal();
    }
  };

  const closeModal = (): void => {
    dismissModalHandler();
  };

  // If modal is active add class to body to prevent scroll
  React.useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add('modalActive');
      document.addEventListener('keyup', handleEscapeKeyPress);
    } else {
      document.body.classList.remove('modalActive');
      document.removeEventListener('keyup', handleEscapeKeyPress);
    }
  }, [isModalOpen]);

  // If user logs in while modal is open, close it
  React.useEffect(() => {
    if (isAuthenticated && isModalOpen) {
      setIsModalOpen(false);
    }
  }, [isAuthenticated, isModalOpen]);

  return ReactDOM.createPortal(
    <Transition
      show={isModalOpen}
      className="fixed inset-0 overflow-y-auto"
      style={{ zIndex: 9999 }}
      as="div"
    >
      <div className="block items-end justify-center md:mx-5 min-h-screen text-center">
        <TransitionChild
          as="div"
          enter="ease-out duration-400 md:duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 transition-opacity">
            <div
              onClick={() => {
                setIsModalOpen(false);
              }}
              className="absolute inset-0 bg-black opacity-80"
            />
          </div>
        </TransitionChild>

        <TransitionChild
          className="inline-block text-left bg-primary-main rounded-2xl shadow-xl transform transition-all align-middle w-full md:w-[500px] relative overflow-hidden mt-[50px] sm:mt-40 border-2 border-secondary-main"
          as="div"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
          enter="ease-out duration-400 md:duration-300"
          enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enterTo="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-y-0 sm:scale-100"
          leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div className="absolute top-3 right-3 z-10">
            <button
              className="bg-gray-xxlight opacity-80 hover:opacity-100 focus:outline-none rounded-full p-2 flex items-center justify-center"
              aria-label="Close"
              aria-describedby="exit"
              data-testid="close-middleman-modal"
              onClick={dismissModalHandler}
            >
              <svg
                className="w-4 h-4 text-black"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div className="flex flex-col p-8 text-center">
            {/* Top border text */}
            <div className="border-b border-secondary-main mb-8 pb-2">
              <Typography
                component="p"
                variant="subtitle2"
                className="text-secondary-main font-semibold tracking-wider text-lg md:text-xl"
              >
                R.I.P. SERVICE FEES
              </Typography>
            </div>

            {/* Main content */}
            <div className="space-y-6 mb-8">
              <div className="font-ceraStencil text-secondary-main text-2xl md:text-4xl font-bold tracking-wide leading-tight">
                T.H.E. MIDDLEMAN
                <br />
                IS DEAD.
              </div>

              <Typography
                component="h3"
                variant="title4"
                className="text-secondary-main font-bold tracking-wide text-2xl md:text-3xl"
              >
                THE SAVVY ERA
                <br />
                HAS BEGUN.
              </Typography>

              <div className="space-y-0">
                <Typography
                  component="p"
                  variant="body2"
                  className="text-secondary-main mt-6 font-sans !font-[Roobert] text-md md:text-lg"
                >
                  T.H.E. Middleman's loss is your $50 gain.
                </Typography>
                <Typography
                  component="p"
                  variant="body2"
                  className="text-secondary-main mt-6 font-sans !font-[Roobert] text-md md:text-lg"
                >
                  Create a free account today and get $50 cash back on your
                  first stay.
                </Typography>
              </div>
            </div>

            {/* Button */}
            <div className="mb-8 w-2/3 min-w-[200px] mx-auto ">
              <Button
                color="primary"
                variant="filled"
                size="lg"
                className="w-full rounded-full justify-center min-w-[200px] !font-[Roobert]"
                onClick={() => {
                  // Fix for the linter error - routes.signup is a function that returns a string
                  window.location.href = routes.middleman;
                  closeModal();
                }}
              >
                Create Your Account
              </Button>
            </div>

            {/* Bottom border text */}
            <div className="border-t border-secondary-main pt-2">
              <Typography
                component="p"
                variant="subtitle2"
                className="text-secondary-main font-semibold tracking-wider text-lg md:text-xl"
              >
                R.I.P. MIDDLEMAN
              </Typography>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Transition>,
    document.getElementById('middleman-modal-root') as Element
  );
};

export default MiddlemanModalPrivate;
